import React, { useEffect, useState } from "react";
import client, { getClients, getClientDetail, getAllAppointments, updateAppointment, addNote, getOrders, updateOrder, resyncCatalog, getCatalogStatus, createAdminAppointment, changePassword, getInstagramLinks, setInstagramLinks, getLoyaltyRewards, getSchedule } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { format, parseISO, isSameDay } from "date-fns";
import { Loader2, LogOut, Users, CalendarDays, Inbox, Plus, PackageCheck, ExternalLink, RefreshCw, Settings, KeyRound, Instagram, Trash2, Gift, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["pending", "confirmed", "completed", "cancelled"];
const ADMIN_SVC = ["CO2 Fractional Laser", "IPL Photofacial", "Wet Cupping", "Dry Cupping", "Moving Cupping", "PRP Therapy", "IV Hydration Therapy", "Weight Management", "Mesotherapy", "HIFU Skin Lifting", "Free Consultation"];
const FULFILL = ["new", "ordered_on_atomy", "shipped", "delivered"];
const TABS = [
  { key: "calendar", label: "Calendar", icon: CalendarClock },
  { key: "orders", label: "Orders", icon: PackageCheck },
  { key: "clients", label: "Clients", icon: Users },
  { key: "appointments", label: "Appointments", icon: CalendarDays },
  { key: "consultations", label: "Consultations", icon: Inbox },
  { key: "settings", label: "Settings", icon: Settings },
];

export default function Admin() {
  const { logout } = useAuth();
  const [tab, setTab] = useState("calendar");
  const [clients, setClients] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [catalog, setCatalog] = useState(null);
  const [markupInput, setMarkupInput] = useState(25);
  const [syncing, setSyncing] = useState(false);

  const loadCatalog = async () => {
    try {
      const s = await getCatalogStatus();
      setCatalog(s);
      if (s.markup_percent != null) setMarkupInput(s.markup_percent);
    } catch { /* ignore */ }
  };

  const doResync = async () => {
    setSyncing(true);
    try {
      await resyncCatalog(Number(markupInput));
      toast.info("Re-syncing your Atomy catalog…");
      const poll = setInterval(async () => {
        const s = await getCatalogStatus();
        setCatalog(s);
        if (s.status !== "running") {
          clearInterval(poll);
          setSyncing(false);
          toast[s.status === "done" ? "success" : "error"](
            s.status === "done" ? `Catalog synced — ${s.product_count} products at +${s.markup_percent}%` : "Sync failed");
          loadAll();
        }
      }, 3000);
    } catch {
      setSyncing(false);
      toast.error("Could not start sync");
    }
  };

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, a, co, o] = await Promise.all([
        getClients(), getAllAppointments(),
        client.get("/consultations").then((r) => r.data), getOrders(),
      ]);
      setClients(c); setAppointments(a); setConsultations(co); setOrders(o);
    } catch {
      toast.error("Failed to load admin data");
    } finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); loadCatalog(); }, []);

  const setFulfill = async (sid, status) => {
    await updateOrder(sid, status);
    setOrders((prev) => prev.map((o) => (o.session_id === sid ? { ...o, fulfillment_status: status } : o)));
    toast.success("Order updated");
  };

  const openClient = async (id) => {
    setDetail("loading");
    try { setDetail(await getClientDetail(id)); } catch { toast.error("Could not load client"); setDetail(null); }
  };

  const setStatus = async (id, status) => {
    const res = await updateAppointment(id, status);
    setAppointments((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    if (res && res.loyalty_code) {
      toast.success(`Loyalty milestone reached — code ${res.loyalty_code} texted to client`);
    } else {
      toast.success(status === "completed" ? "Marked completed" : "Status updated");
    }
  };

  const [apptForm, setApptForm] = useState({ name: "", phone: "", email: "", service: "Wet Cupping", date: "", time: "", notes: "", sms_consent: false });
  const setAF = (k) => (e) => setApptForm((f) => ({ ...f, [k]: e.target.value }));
  const [addingAppt, setAddingAppt] = useState(false);
  const isWetAppt = apptForm.service.toLowerCase().includes("wet");
  const doAddAppt = async (e) => {
    e.preventDefault();
    if (!apptForm.name || !apptForm.phone || !apptForm.service || !apptForm.date || !apptForm.time) {
      toast.error("Name, phone, service, date and time are required."); return;
    }
    if (isWetAppt && !apptForm.sms_consent) {
      toast.error("Confirm the client has consented to receive SMS pre/after-care."); return;
    }
    setAddingAppt(true);
    try {
      const res = await createAdminAppointment(apptForm);
      const wet = apptForm.service.toLowerCase().includes("wet");
      toast.success(wet
        ? (res.sms_status === "sent" ? "Appointment added — pre-care SMS sent" : "Appointment added (SMS pending Twilio setup)")
        : "Appointment added");
      setApptForm({ name: "", phone: "", email: "", service: "Wet Cupping", date: "", time: "", notes: "", sms_consent: false });
      loadAll();
    } catch {
      toast.error("Could not add appointment");
    } finally { setAddingAppt(false); }
  };

  const submitNote = async () => {
    if (!note.trim() || !detail?.user) return;
    const n = await addNote({ client_id: detail.user.id, note });
    setDetail((d) => ({ ...d, notes: [n, ...d.notes] }));
    setNote("");
    toast.success("Note added");
  };

  // ---- Settings tab: Change password + Instagram links ----
  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const doChangePw = async (e) => {
    e.preventDefault();
    if (pwForm.next.length < 8) { toast.error("Use at least 8 characters"); return; }
    if (pwForm.next !== pwForm.confirm) { toast.error("Passwords do not match"); return; }
    setPwSaving(true);
    try {
      await changePassword({ current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password updated");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change password");
    } finally { setPwSaving(false); }
  };

  const [ig, setIg] = useState({ handle: "womenheavenmedspa", posts: [] });
  const [igSaving, setIgSaving] = useState(false);
  const [rewards, setRewards] = useState([]);
  const loadIg = async () => {
    try { setIg(await getInstagramLinks()); } catch { /* ignore */ }
  };
  const loadRewards = async () => {
    try { setRewards(await getLoyaltyRewards()); } catch { /* ignore */ }
  };
  const [schedule, setSchedule] = useState([]);
  const [scheduleDay, setScheduleDay] = useState(new Date());
  const loadSchedule = async () => {
    try { setSchedule(await getSchedule()); } catch { /* ignore */ }
  };
  useEffect(() => { if (tab === "settings") { loadIg(); loadRewards(); } if (tab === "calendar") loadSchedule(); }, [tab]);
  const addIgRow = () => setIg((s) => ({ ...s, posts: [...s.posts, { url: "", image: "", caption: "" }] }));
  const rmIgRow = (i) => setIg((s) => ({ ...s, posts: s.posts.filter((_, idx) => idx !== i) }));
  const editIg = (i, k, v) => setIg((s) => ({ ...s, posts: s.posts.map((p, idx) => idx === i ? { ...p, [k]: v } : p) }));
  const saveIg = async () => {
    const clean = ig.posts.filter((p) => p.image && p.image.trim());
    if (clean.length === 0) { toast.error("Add at least one post image"); return; }
    setIgSaving(true);
    try {
      const res = await setInstagramLinks({ handle: ig.handle, posts: clean });
      setIg(res);
      toast.success("Instagram feed updated");
    } catch {
      toast.error("Could not save");
    } finally { setIgSaving(false); }
  };

  return (
    <main className="pt-28 pb-28 min-h-screen relative z-10">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="overline text-[#9A7420] mb-2">Admin Console</p>
            <h1 className="font-display text-5xl tracking-tight">Women Heaven</h1>
          </div>
          <button data-testid="admin-logout" onClick={logout} className="btn-secondary py-2 px-5 text-sm"><LogOut className="w-4 h-4" /> Logout</button>
        </div>

        <div className="flex gap-3 mb-10 border-b border-[#DAD5E2]">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 -mb-px border-b-2 transition-colors ${tab === t.key ? "border-[#9A7420] text-[#9A7420]" : "border-transparent text-[#57515F]"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-[#9A7420]" />
        ) : (
          <>
            {tab === "calendar" && (() => {
              const busyDates = new Set(schedule.map((e) => e.date));
              const dayStr = format(scheduleDay, "yyyy-MM-dd");
              const dayEvents = schedule.filter((e) => e.date === dayStr).sort((a, b) => (a.time || "").localeCompare(b.time || ""));
              const fmtTime = (t) => {
                if (!t) return "—";
                const [h, m] = t.split(":").map(Number);
                return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}`;
              };
              return (
                <div className="grid lg:grid-cols-[auto_1fr] gap-8" data-testid="calendar-panel">
                  <div className="bg-white border border-[#DAD5E2] rounded-2xl p-4 self-start">
                    <CalendarPicker
                      mode="single"
                      selected={scheduleDay}
                      onSelect={(d) => d && setScheduleDay(d)}
                      modifiers={{ busy: (d) => busyDates.has(format(d, "yyyy-MM-dd")) }}
                      modifiersClassNames={{ busy: "relative after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-1.5 after:h-1.5 after:rounded-full after:bg-[#9A7420]" }}
                      initialFocus
                    />
                    <div className="mt-4 flex items-center gap-2 text-xs text-[#57515F]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#9A7420]" />
                      Days with bookings
                    </div>
                  </div>
                  <div className="bg-white border border-[#DAD5E2] rounded-2xl p-6">
                    <div className="flex items-baseline justify-between mb-5">
                      <div>
                        <p className="overline text-[#9A7420]">{format(scheduleDay, "EEEE")}</p>
                        <h3 className="font-display text-3xl text-[#2B2340]">{format(scheduleDay, "MMMM d, yyyy")}</h3>
                      </div>
                      <span className="text-xs text-[#8B8494]">{dayEvents.length} {dayEvents.length === 1 ? "booking" : "bookings"}</span>
                    </div>
                    {dayEvents.length === 0 ? (
                      <p className="text-[#8B8494] text-sm py-10 text-center">No bookings for this day.</p>
                    ) : (
                      <ul className="space-y-3" data-testid="day-events">
                        {dayEvents.map((e) => (
                          <li key={e.id} className="border border-[#DAD5E2] rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                            <div className="w-20 shrink-0">
                              <p className="font-display text-xl text-[#2B2340] leading-none">{fmtTime(e.time)}</p>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-[#2B2340] truncate">{e.name}</p>
                                <span className={`text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full ${e.kind === "consultation" ? "bg-[#EDEAF2] text-[#4A3D6B]" : "bg-[#F8F5EE] text-[#9A7420] border border-[#E4CE8F]/50"}`}>
                                  {e.kind === "consultation" ? "Consult" : e.status || "appt"}
                                </span>
                              </div>
                              <p className="text-sm text-[#57515F] truncate">{e.service}</p>
                              <p className="text-xs text-[#8B8494] mt-0.5">{e.phone || e.email || "—"}</p>
                              {e.notes && <p className="text-xs text-[#8B8494] italic mt-1 truncate">{e.notes}</p>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              );
            })()}

            {tab === "orders" && (
              <div className="space-y-4" data-testid="orders-list">
                <div className="bg-[#2B2340] text-white rounded-2xl p-6 flex flex-col md:flex-row md:items-center gap-5 justify-between">
                  <div>
                    <p className="overline text-[#DCC17C] mb-1">Atomy Catalog</p>
                    <p className="text-white/80 text-sm">
                      {catalog?.product_count ?? "—"} products · current markup <b className="text-[#DCC17C]">+{catalog?.markup_percent ?? markupInput}%</b>
                      {catalog?.last_synced && <span className="text-white/40"> · synced {catalog.last_synced.slice(0, 10)}</span>}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center bg-white/10 rounded-full px-4 py-2">
                      <span className="text-white/60 text-sm mr-1">Markup</span>
                      <input data-testid="markup-input" type="number" min="0" max="500" value={markupInput}
                        onChange={(e) => setMarkupInput(e.target.value)}
                        className="w-14 bg-transparent text-white text-right outline-none" />
                      <span className="text-white/60 text-sm">%</span>
                    </div>
                    <button data-testid="resync-button" onClick={doResync} disabled={syncing}
                      className="btn-primary bg-[#DCC17C] text-[#2B2340] py-2.5 px-5 text-sm disabled:opacity-60">
                      {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <><RefreshCw className="w-4 h-4" /> Re-sync Catalog</>}
                    </button>
                  </div>
                </div>
                {orders.length === 0 && <p className="text-[#8B8494]">No paid orders yet.</p>}
                {orders.map((o) => {
                  const a = o.ship_address || {};
                  const addr = [a.line1, a.line2, a.city, a.state, a.postal_code, a.country].filter(Boolean).join(", ");
                  return (
                    <div key={o.session_id} data-testid={`order-${o.session_id}`} className="bg-white border border-[#DAD5E2] rounded-2xl p-6 grid md:grid-cols-3 gap-6">
                      <div>
                        <p className="overline text-[#9A7420] mb-1">Order</p>
                        {(o.items && o.items.length ? o.items : [{ product_name: o.product_name, quantity: o.quantity, atomy_product_no: o.atomy_product_no, atomy_url: o.atomy_url }]).map((it, idx) => (
                          <div key={idx} className="mb-2">
                            <p className="font-display text-xl leading-tight">{it.product_name} <span className="text-sm text-[#57515F]">×{it.quantity || 1}</span></p>
                            {it.atomy_product_no && (
                              <a href={it.atomy_url} target="_blank" rel="noreferrer" data-testid={idx === 0 ? `atomy-link-${o.session_id}` : undefined}
                                className="inline-flex items-center gap-1 text-xs text-[#9A7420] underline">
                                Order on Atomy — {it.atomy_product_no} <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        ))}
                        <p className="text-sm text-[#57515F] mt-1">Total ${Number(o.amount).toFixed(2)} {String(o.currency).toUpperCase()}</p>
                      </div>
                      <div>
                        <p className="overline text-[#9A7420] mb-1">Ship To</p>
                        <p className="font-medium">{o.ship_name || o.client_name || "—"}</p>
                        <p className="text-sm text-[#57515F]">{o.ship_email || o.client_email}</p>
                        <p className="text-sm text-[#57515F]">{o.ship_phone || "—"}</p>
                        <p className="text-sm text-[#57515F] mt-1">{addr || "No address captured"}</p>
                      </div>
                      <div className="flex flex-col justify-between">
                        <div>
                          <p className="overline text-[#9A7420] mb-1">Fulfillment</p>
                          <Select value={o.fulfillment_status || "new"} onValueChange={(v) => setFulfill(o.session_id, v)}>
                            <SelectTrigger data-testid={`order-status-${o.session_id}`} className="capitalize"><SelectValue /></SelectTrigger>
                            <SelectContent>{FULFILL.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-[#8B8494] mt-3">{o.updated_at?.slice(0, 10)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {tab === "clients" && (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="clients-grid">
                {clients.length === 0 && <p className="text-[#8B8494]">No clients yet.</p>}
                {clients.map((c) => (
                  <button key={c.id} data-testid={`client-${c.id}`} onClick={() => openClient(c.id)}
                    className="text-left bg-white border border-[#DAD5E2] rounded-2xl p-6 hover:shadow-lg transition-shadow">
                    <p className="font-display text-2xl">{c.name}</p>
                    <p className="text-sm text-[#57515F]">{c.email}</p>
                    <p className="text-sm text-[#57515F]">{c.phone || "—"}</p>
                    <div className="flex gap-4 mt-4 text-xs text-[#8B8494]">
                      <span>{c.analyses_count} scans</span>
                      <span>{c.appointments_count} appts</span>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {tab === "appointments" && (
              <div className="space-y-6">
                <form data-testid="add-appointment-form" onSubmit={doAddAppt} className="bg-white border border-[#DAD5E2] rounded-2xl p-6">
                  <p className="overline text-[#9A7420] mb-4">Add an appointment for a client</p>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-7 gap-3 items-end">
                    <input data-testid="af-name" className="input-line" placeholder="Client name" value={apptForm.name} onChange={setAF("name")} />
                    <input data-testid="af-phone" className="input-line" placeholder="Phone" value={apptForm.phone} onChange={setAF("phone")} />
                    <input data-testid="af-email" type="email" className="input-line" placeholder="Email (optional)" value={apptForm.email} onChange={setAF("email")} />
                    <Select value={apptForm.service} onValueChange={(v) => setApptForm((f) => ({ ...f, service: v }))}>
                      <SelectTrigger data-testid="af-service"><SelectValue placeholder="Service" /></SelectTrigger>
                      <SelectContent>{ADMIN_SVC.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                    <input data-testid="af-date" type="date" className="input-line" value={apptForm.date} onChange={setAF("date")} />
                    <input data-testid="af-time" type="time" className="input-line" value={apptForm.time} onChange={setAF("time")} />
                    <button data-testid="af-submit" type="submit" disabled={addingAppt} className="btn-primary justify-center">
                      {addingAppt ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /> Add</>}
                    </button>
                  </div>
                  <p className="text-xs text-[#8B8494] mt-3">
                    {apptForm.email && <>A confirmation email will be sent to <b>{apptForm.email}</b>. </>}
                    {apptForm.service.toLowerCase().includes("wet") && "Wet cupping selected — pre-care SMS on add, after-care on Complete."}
                  </p>
                  {isWetAppt && (
                    <label data-testid="af-sms-consent-block" className="flex gap-3 items-start text-xs text-[#57515F] bg-[#F8F5EE] border border-[#E4CE8F]/60 rounded-xl p-3 mt-4 cursor-pointer">
                      <input data-testid="af-sms-consent" type="checkbox" checked={apptForm.sms_consent}
                        onChange={(e) => setApptForm((f) => ({ ...f, sms_consent: e.target.checked }))} className="mt-0.5 accent-[#9A7420]" />
                      <span>
                        Client has consented (verbally or in writing) to receive up to 2 SMS messages for this Wet Cupping appointment. STOP to opt out. Msg &amp; data rates apply.
                      </span>
                    </label>
                  )}
                </form>

                <div className="overflow-x-auto border border-[#DAD5E2] rounded-2xl bg-white" data-testid="appointments-table">
                  <table className="w-full text-sm">
                    <thead className="bg-[#E4E0EB] text-left text-[#57515F]">
                      <tr>{["Client", "Phone", "Service", "Date", "Time", "Status"].map((h) => <th key={h} className="px-5 py-4 font-medium whitespace-nowrap">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {appointments.length === 0 && <tr><td className="px-5 py-6 text-[#8B8494]" colSpan={6}>No appointments.</td></tr>}
                      {appointments.map((a) => (
                        <tr key={a.id} className="border-t border-[#DAD5E2]">
                          <td className="px-5 py-4">{a.name}{a.email && <div className="text-xs text-[#8B8494]">{a.email}</div>}</td>
                          <td className="px-5 py-4 whitespace-nowrap">{a.phone || "—"}</td>
                          <td className="px-5 py-4">{a.service}</td>
                          <td className="px-5 py-4">{a.date}</td>
                          <td className="px-5 py-4">{a.time}</td>
                          <td className="px-5 py-4">
                            <Select value={a.status} onValueChange={(v) => setStatus(a.id, v)}>
                              <SelectTrigger data-testid={`appt-status-${a.id}`} className="w-36 capitalize"><SelectValue /></SelectTrigger>
                              <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                            </Select>
                          </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            )}

            {tab === "consultations" && (
              <div className="overflow-x-auto border border-[#DAD5E2] rounded-2xl bg-white" data-testid="consultations-table">
                <table className="w-full text-sm">
                  <thead className="bg-[#E4E0EB] text-left text-[#57515F]">
                    <tr>{["Name", "Email", "Phone", "Service", "Date", "Message"].map((h) => <th key={h} className="px-5 py-4 font-medium whitespace-nowrap">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {consultations.length === 0 && <tr><td className="px-5 py-6 text-[#8B8494]" colSpan={6}>No requests.</td></tr>}
                    {consultations.map((r) => (
                      <tr key={r.id} className="border-t border-[#DAD5E2]">
                        <td className="px-5 py-4 whitespace-nowrap">{r.name}</td>
                        <td className="px-5 py-4 whitespace-nowrap">{r.email}</td>
                        <td className="px-5 py-4 whitespace-nowrap">{r.phone}</td>
                        <td className="px-5 py-4 whitespace-nowrap">{r.service}</td>
                        <td className="px-5 py-4 whitespace-nowrap">{r.preferred_date || "—"}</td>
                        <td className="px-5 py-4 max-w-xs truncate">{r.message || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "settings" && (
              <div className="grid lg:grid-cols-2 gap-8" data-testid="settings-panel">
                {/* Change password */}
                <form data-testid="change-password-form" onSubmit={doChangePw} className="bg-white border border-[#DAD5E2] rounded-2xl p-7">
                  <div className="flex items-center gap-2 mb-5">
                    <KeyRound className="w-4 h-4 text-[#9A7420]" strokeWidth={1.8} />
                    <p className="overline text-[#9A7420]">Change password</p>
                  </div>
                  <div className="space-y-5">
                    <div>
                      <label className="overline block mb-2">Current password</label>
                      <input data-testid="cp-current" type="password" required className="input-line"
                        value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} />
                    </div>
                    <div>
                      <label className="overline block mb-2">New password</label>
                      <input data-testid="cp-next" type="password" required minLength={8} className="input-line"
                        value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} />
                    </div>
                    <div>
                      <label className="overline block mb-2">Confirm new password</label>
                      <input data-testid="cp-confirm" type="password" required minLength={8} className="input-line"
                        value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
                    </div>
                    <button data-testid="cp-submit" type="submit" disabled={pwSaving} className="btn-primary w-full justify-center">
                      {pwSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update password"}
                    </button>
                  </div>
                </form>

                {/* Instagram feed manager */}
                <div className="bg-white border border-[#DAD5E2] rounded-2xl p-7" data-testid="instagram-manager">
                  <div className="flex items-center gap-2 mb-5">
                    <Instagram className="w-4 h-4 text-[#9A7420]" strokeWidth={1.8} />
                    <p className="overline text-[#9A7420]">Instagram feed</p>
                  </div>
                  <div className="mb-4">
                    <label className="overline block mb-2">Handle (without @)</label>
                    <input data-testid="ig-handle-input" className="input-line" value={ig.handle || ""}
                      onChange={(e) => setIg({ ...ig, handle: e.target.value })} />
                  </div>
                  <p className="text-xs text-[#8B8494] mb-3">Paste up to 24 posts. Each row: post URL (from Instagram) + a public image URL that will show on your site.</p>
                  <div className="space-y-3 mb-4 max-h-[420px] overflow-y-auto pr-1">
                    {ig.posts.map((p, i) => (
                      <div key={i} className="grid grid-cols-[64px_1fr_28px] gap-2 items-center">
                        {p.image ? (
                          <img src={p.image} alt="preview" className="w-16 h-16 object-cover rounded-md border border-[#DAD5E2]" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                        ) : (
                          <div className="w-16 h-16 rounded-md bg-[#EDEAF2] border border-[#DAD5E2]" />
                        )}
                        <div className="space-y-1">
                          <input data-testid={`ig-image-${i}`} className="input-line py-1 text-xs" placeholder="Image URL (jpg/png)"
                            value={p.image} onChange={(e) => editIg(i, "image", e.target.value)} />
                          <input data-testid={`ig-url-${i}`} className="input-line py-1 text-xs" placeholder="Instagram post URL"
                            value={p.url} onChange={(e) => editIg(i, "url", e.target.value)} />
                        </div>
                        <button data-testid={`ig-remove-${i}`} type="button" onClick={() => rmIgRow(i)}
                          className="text-[#8B8494] hover:text-[#B54B4B]"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <button data-testid="ig-add-row" type="button" onClick={addIgRow} className="btn-secondary py-2 px-4 text-sm">
                      <Plus className="w-4 h-4" /> Add post
                    </button>
                    <button data-testid="ig-save" type="button" onClick={saveIg} disabled={igSaving} className="btn-primary py-2 px-5 text-sm ml-auto">
                      {igSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save feed"}
                    </button>
                  </div>
                </div>

                {/* Loyalty rewards issued */}
                <div className="bg-white border border-[#DAD5E2] rounded-2xl p-7 lg:col-span-2" data-testid="loyalty-panel">
                  <div className="flex items-center gap-2 mb-2">
                    <Gift className="w-4 h-4 text-[#9A7420]" strokeWidth={1.8} />
                    <p className="overline text-[#9A7420]">Loyalty rewards</p>
                  </div>
                  <p className="text-xs text-[#8B8494] mb-5">
                    Clients get a warm SMS + discount code automatically when they complete their <b>3rd</b> (10% off) and <b>5th</b> (15% off) cupping session. Codes are one-time — honor them in-spa or when they order online.
                  </p>
                  {rewards.length === 0 ? (
                    <p className="text-[#8B8494] text-sm">No rewards issued yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="loyalty-table">
                        <thead className="text-left text-[#57515F] bg-[#F5F2FA]">
                          <tr>
                            {["Client", "Phone", "Sessions", "Code", "Discount", "Sent", "Issued"].map((h) =>
                              <th key={h} className="px-4 py-3 font-medium whitespace-nowrap">{h}</th>)}
                          </tr>
                        </thead>
                        <tbody>
                          {rewards.map((r) => (
                            <tr key={r.id} className="border-t border-[#DAD5E2]">
                              <td className="px-4 py-3">{r.name || "—"}</td>
                              <td className="px-4 py-3 whitespace-nowrap">{r.phone}</td>
                              <td className="px-4 py-3">{r.sessions}</td>
                              <td className="px-4 py-3 font-mono text-[#2B2340]">{r.code}</td>
                              <td className="px-4 py-3">{r.discount_percent}%</td>
                              <td className="px-4 py-3">
                                <span className={`text-xs px-2 py-1 rounded-full ${r.sms_status === "sent" ? "bg-[#3E5C4E]/15 text-[#2f4a3d]" : "bg-[#E4CE8F]/40 text-[#7a5c12]"}`}>
                                  {r.sms_status}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-[#8B8494] whitespace-nowrap">{r.created_at?.slice(0, 10)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Client detail drawer */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent data-testid="client-detail" className="bg-[#EDEAF2] border-l border-[#DAD5E2] w-full sm:max-w-lg overflow-y-auto">
          <SheetTitle className="sr-only">Client details</SheetTitle>
          {detail === "loading" || !detail ? (
            <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-[#9A7420]" /></div>
          ) : (
            <>
              <SheetHeader>
                <SheetTitle className="font-display text-3xl text-left">{detail.user.name}</SheetTitle>
              </SheetHeader>
              <p className="text-sm text-[#57515F] mb-6">{detail.user.email} · {detail.user.phone || "no phone"}</p>

              <h4 className="overline mb-3">Skin Analyses</h4>
              {detail.analyses.length === 0 ? <p className="text-[#8B8494] text-sm mb-6">None</p> : (
                <div className="space-y-3 mb-8">
                  {detail.analyses.map((an) => (
                    <div key={an.id} className="bg-white rounded-xl p-3 flex gap-3 items-center">
                      {an.image && <img src={an.image} alt="selfie" className="w-14 h-16 object-cover rounded-md" />}
                      <div className="text-sm">
                        <span className="font-display text-xl">{an.result?.overall_score}/100</span>
                        <p className="text-[#57515F]">{an.result?.skin_type} · {an.created_at?.slice(0, 10)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <h4 className="overline mb-3">Appointments</h4>
              {detail.appointments.length === 0 ? <p className="text-[#8B8494] text-sm mb-6">None</p> : (
                <ul className="space-y-2 mb-8 text-sm">
                  {detail.appointments.map((a) => (
                    <li key={a.id} className="bg-white rounded-lg p-3 flex justify-between">
                      <span>{a.service} — {a.date} {a.time}</span>
                      <span className="capitalize text-[#9A7420]">{a.status}</span>
                    </li>
                  ))}
                </ul>
              )}

              <h4 className="overline mb-3">Orders</h4>
              {detail.payments.length === 0 ? <p className="text-[#8B8494] text-sm mb-6">None</p> : (
                <ul className="space-y-2 mb-8 text-sm">
                  {detail.payments.map((p) => (
                    <li key={p.session_id} className="bg-white rounded-lg p-3 flex justify-between">
                      <span>{p.product_name}</span>
                      <span>${Number(p.amount).toFixed(2)} · {p.payment_status}</span>
                    </li>
                  ))}
                </ul>
              )}

              <h4 className="overline mb-3">Treatment Notes</h4>
              <div className="space-y-2 mb-4">
                {detail.notes.map((n) => (
                  <div key={n.id} className="bg-white rounded-lg p-3 text-sm">
                    <p>{n.note}</p>
                    <p className="text-xs text-[#8B8494] mt-1">{n.created_at?.slice(0, 10)}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input data-testid="note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a treatment note…" className="input-line flex-1" />
                <button data-testid="note-add" onClick={submitNote} className="btn-primary px-4"><Plus className="w-4 h-4" /></button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </main>
  );
}
